/**
 * Created by vadimdez on 21/06/16.
 */

import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {PDFDocumentProxy, PDFPageProxy, PDFProgressData, PDFPromise, PDFSource, PDFViewerParams} from 'pdfjs-dist';

import {createEventBus} from '../utils/event-bus-utils';

let /** @type {?} */ PDFJS: any;
let /** @type {?} */ PDFJSViewer: any;
/**
 * @return {?}
 */
function isSSR() {
  return typeof window === 'undefined';
}

if (!isSSR()) {
  PDFJS = require('pdfjs-dist/build/pdf');
  PDFJSViewer = require('pdfjs-dist/web/pdf_viewer');

  PDFJS.verbosity = PDFJS.VerbosityLevel.ERRORS;
}
export type RenderTextMode = number;
export let RenderTextMode: any = {};
RenderTextMode.DISABLED = 0;
RenderTextMode.ENABLED = 1;
RenderTextMode.ENHANCED = 2;
RenderTextMode[RenderTextMode.DISABLED] = "DISABLED";
RenderTextMode[RenderTextMode.ENABLED] = "ENABLED";
RenderTextMode[RenderTextMode.ENHANCED] = "ENHANCED";

export class PdfViewerComponent implements OnChanges, OnInit, OnDestroy {
  static CSS_UNITS: number = 96.0 / 72.0;
private pdfMultiPageViewer: any;
private pdfMultiPageLinkService: any;
private pdfMultiPageFindController: any;
private pdfSinglePageViewer: any;
private pdfSinglePageLinkService: any;
private pdfSinglePageFindController: any;
private _cMapsUrl = `https://unpkg.com/pdfjs-dist@${ ( /** @type {?} */((PDFJS as any))).version }/cmaps/`;
private _renderText = true;
private _renderTextMode: RenderTextMode = RenderTextMode.ENABLED;
private _stickToPage = false;
private _originalSize = true;
private _pdf: PDFDocumentProxy;
private _page = 1;
private _zoom = 1;
private _rotation = 0;
private _showAll = true;
private _canAutoResize = true;
private _fitToPage = false;
private _externalLinkTarget = 'blank';
private lastLoaded: string | Uint8Array | PDFSource;
private resizeTimeout: NodeJS.Timer;

   afterLoadComplete = new EventEmitter<PDFDocumentProxy>();
   pageRendered = new EventEmitter<CustomEvent>();
   textLayerRendered = new EventEmitter<CustomEvent>();
   onError = new EventEmitter<any>();
   onProgress = new EventEmitter<PDFProgressData>();
   pageChange: EventEmitter<number> = new EventEmitter<number>(true);
  
  src: string | Uint8Array | PDFSource;
private pdfLoadingTask: any;
/**
 * @param {?} cMapsUrl
 * @return {?}
 */
set cMapsUrl(cMapsUrl: string) {
    this._cMapsUrl = cMapsUrl;
  }
/**
 * @param {?} _page
 * @return {?}
 */
set page(_page) {
    _page = parseInt(_page, 10) || 1;

    if (this._pdf) {
      _page = this.getValidPageNumber(_page);
    }

    this._page = _page;
    this.pageChange.emit(_page);
  }
/**
 * @param {?} renderText
 * @return {?}
 */
set renderText(renderText: boolean) {
    this._renderText = renderText;
  }
/**
 * @param {?} renderTextMode
 * @return {?}
 */
set renderTextMode(renderTextMode: RenderTextMode) {
    this._renderTextMode = renderTextMode;
  }
/**
 * @param {?} originalSize
 * @return {?}
 */
set originalSize(originalSize: boolean) {
    this._originalSize = originalSize;
  }
/**
 * @param {?} value
 * @return {?}
 */
set showAll(value: boolean) {
    this._showAll = value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set stickToPage(value: boolean) {
    this._stickToPage = value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set zoom(value: number) {
    if (value <= 0) {
      return;
    }

    this._zoom = value;
  }
/**
 * @return {?}
 */
get zoom() {
    return this._zoom;
  }
/**
 * @param {?} value
 * @return {?}
 */
set rotation(value: number) {
    if (!(typeof value === 'number' && value % 90 === 0)) {
      console.warn('Invalid pages rotation angle.');
      return;
    }

    this._rotation = value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set externalLinkTarget(value: string) {
    this._externalLinkTarget = value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set autoresize(value: boolean) {
    this._canAutoResize = Boolean(value);
  }
/**
 * @param {?} value
 * @return {?}
 */
set fitToPage(value: boolean) {
    this._fitToPage = Boolean(value);
  }
/**
 * @param {?} type
 * @return {?}
 */
static getLinkTarget(type: string) {
    switch (type) {
      case 'blank':
        return ( /** @type {?} */((<any>PDFJS))).LinkTarget.BLANK;
      case 'none':
        return ( /** @type {?} */((<any>PDFJS))).LinkTarget.NONE;
      case 'self':
        return ( /** @type {?} */((<any>PDFJS))).LinkTarget.SELF;
      case 'parent':
        return ( /** @type {?} */((<any>PDFJS))).LinkTarget.PARENT;
      case 'top':
        return ( /** @type {?} */((<any>PDFJS))).LinkTarget.TOP;
    }

    return null;
  }
/**
 * @param {?} type
 * @return {?}
 */
static setExternalLinkTarget(type: string) {
    const /** @type {?} */ linkTarget = PdfViewerComponent.getLinkTarget(type);

    if (linkTarget !== null) {
      ( /** @type {?} */((<any>PDFJS))).externalLinkTarget = linkTarget;
    }
  }
/**
 * @param {?} element
 */
constructor(private element: ElementRef) {
    if (isSSR()) {
      return;
    }

    let pdfWorkerSrc: string;

    if (window.hasOwnProperty('pdfWorkerSrc') && typeof (window as any).pdfWorkerSrc === 'string' && (window as any).pdfWorkerSrc) {
      pdfWorkerSrc = (window as any).pdfWorkerSrc;
    } else {
      pdfWorkerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${ (PDFJS as any).version }/pdf.worker.min.js`;
    }

    (PDFJS as any).GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

  }
/**
 * @return {?}
 */
ngOnInit() {
    if (!isSSR()) {
      this.setupMultiPageViewer();
      this.setupSinglePageViewer();
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._pdf) {
      this.close().then(() => {
        console.log('Viewer destroyed');
      });
    }
  }
/**
 * @return {?}
 */
public onPageResize() {
    if (!this._canAutoResize || !this._pdf) {
      return;
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.updateSize();
    }, 100);
  }
/**
 * @param {?} e
 * @return {?}
 */
onPageRendered(e: CustomEvent) {
    this.pageRendered.emit(e);
  }
/**
 * @param {?} e
 * @return {?}
 */
onTextLayerRendered(e: CustomEvent) {
    this.textLayerRendered.emit(e);
  }
/**
 * @return {?}
 */
get pdfLinkService(): any {
    return this._showAll ? this.pdfMultiPageLinkService : this.pdfSinglePageLinkService;
  }
/**
 * @return {?}
 */
get pdfViewer(): any {
    return this.getCurrentViewer();
  }
/**
 * @return {?}
 */
get pdfFindController(): any {
    return this._showAll ? this.pdfMultiPageFindController : this.pdfSinglePageFindController;
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    if (isSSR()) {
      return;
    }

    if ('src' in changes) {
      if (this.lastLoaded !== this.src) {
        // pdf source has changed ->  close old pdf document and load new one after
        this.close().then(() => {
          this.loadPDF();
        });
      } else if (this._pdf) {
        // pdf src remained the same and this._pdf exists -> update view
        this.update();
      }
    } else if (this._pdf) {
      if ('renderText' in changes) {
        this.getCurrentViewer().textLayerMode = this._renderText ? this._renderTextMode : RenderTextMode.DISABLED;
        this.resetPdfDocument();
      } else if ('showAll' in changes) {
        this.resetPdfDocument();
      }
      if ('page' in changes) {
        // New form of page changing: The viewer will now jump to the specified page when it is changed.
        // This behavior is introduced by using the PDFSinglePageViewer
        this.getCurrentViewer().scrollPageIntoView({pageNumber: this._page});
      }

      this.update();
    }
  }
/**
 * @return {?}
 */
public updateSize() {
    const /** @type {?} */ currentViewer = this.getCurrentViewer();
    this._pdf.getPage(currentViewer.currentPageNumber).then((page: PDFPageProxy) => {
      const /** @type {?} */ viewport = page.getViewport(this._zoom, this._rotation);
      let /** @type {?} */ scale = this._zoom;
      let /** @type {?} */ stickToPage = true;

      // Scale the document when it shouldn't be in original size or doesn't fit into the viewport
      if (!this._originalSize || (this._fitToPage && viewport.width > this.element.nativeElement.offsetWidth)) {
        scale = this.getScale(page.getViewport(1).width);
        stickToPage = !this._stickToPage;
      }

      currentViewer._setScale(scale, stickToPage);
    });
  }
/**
 * @return {?}
 */
private setupMultiPageViewer() {
    ( /** @type {?} */((PDFJS as any))).disableTextLayer = !this._renderText;

    PdfViewerComponent.setExternalLinkTarget(this._externalLinkTarget);

    this.pdfMultiPageLinkService = new PDFJSViewer.PDFLinkService();

    const /** @type {?} */ pdfOptions: PDFViewerParams | any = {
      eventBus: createEventBus(PDFJSViewer),
      container: this.element.nativeElement.querySelector('div'),
      removePageBorders: true,
      linkService: this.pdfMultiPageLinkService,
      textLayerMode: this._renderText ? this._renderTextMode : RenderTextMode.DISABLED
    };

    this.pdfMultiPageViewer = new PDFJSViewer.PDFViewer(pdfOptions);
    this.pdfMultiPageLinkService.setViewer(this.pdfMultiPageViewer);
    this.pdfMultiPageFindController = new PDFJSViewer.PDFFindController({pdfViewer: this.pdfMultiPageViewer});
    this.pdfMultiPageViewer.setFindController(this.pdfMultiPageFindController);
  }
/**
 * @return {?}
 */
private setupSinglePageViewer() {
    ( /** @type {?} */((PDFJS as any))).disableTextLayer = !this._renderText;

    PdfViewerComponent.setExternalLinkTarget(this._externalLinkTarget);

    this.pdfSinglePageLinkService = new PDFJSViewer.PDFLinkService();

    const /** @type {?} */ pdfOptions: PDFViewerParams | any = {
      eventBus: createEventBus(PDFJSViewer),
      container: this.element.nativeElement.querySelector('div'),
      removePageBorders: true,
      linkService: this.pdfSinglePageLinkService,
      textLayerMode: this._renderText ? this._renderTextMode : RenderTextMode.DISABLED
    };


    this.pdfSinglePageViewer = new PDFJSViewer.PDFSinglePageViewer(pdfOptions);
    this.pdfSinglePageLinkService.setViewer(this.pdfSinglePageViewer);
    this.pdfSinglePageFindController = new PDFJSViewer.PDFFindController({pdfViewer: this.pdfSinglePageViewer});
    this.pdfSinglePageViewer.setFindController(this.pdfSinglePageFindController);
  }
/**
 * @param {?} page
 * @return {?}
 */
private getValidPageNumber(page: number): number {
    if (page < 1) {
      return 1;
    }

    if (page > this._pdf.numPages) {
      return this._pdf.numPages;
    }

    return page;
  }
/**
 * @return {?}
 */
private getDocumentParams() {
    const /** @type {?} */ srcType = typeof(this.src);

    if (!this._cMapsUrl) {
      return this.src;
    }

    const /** @type {?} */ params: any = {
      cMapUrl: this._cMapsUrl,
      cMapPacked: true
    };

    if (srcType === 'string') {
      params.url = this.src;
    } else if (srcType === 'object') {
      if (( /** @type {?} */((this.src as any))).byteLength !== undefined) {
        params.data = this.src;
      } else {
        Object.assign(params, this.src);
      }
    }

    return params;
  }
/**
 * @return {?}
 */
private loadPDF() {
    if (!this.src) {
      return;
    }

    this.pdfLoadingTask = ( /** @type {?} */((PDFJS as any))).getDocument(this.getDocumentParams());

    this.pdfLoadingTask.onProgress = (progressData: PDFProgressData) => {
      this.onProgress.emit(progressData);
    };

    const /** @type {?} */ src = this.src;
    ( /** @type {?} */((<PDFPromise<PDFDocumentProxy>>this.pdfLoadingTask.promise)))
      .then((pdf: PDFDocumentProxy) => {
        if (this._pdf) {
          this._pdf.destroy();
        }
        this._pdf = pdf;
        this.lastLoaded = src;

        this.afterLoadComplete.emit(pdf);

        if (!this.pdfMultiPageViewer) {
          this.setupMultiPageViewer();
          this.setupSinglePageViewer();
        }

        this.resetPdfDocument();

        this.update();
      }, (error: any) => {
        this.onError.emit(error);
      });
  }
/**
 * @return {?}
 */
private update() {
    this.page = this._page;

    this.render();
  }
/**
 * @return {?}
 */
private render() {
    this._page = this.getValidPageNumber(this._page);
    const /** @type {?} */ currentViewer = this.getCurrentViewer();

    if (this._rotation !== 0 || currentViewer.pagesRotation !== this._rotation) {
      setTimeout(() => {
        currentViewer.pagesRotation = this._rotation;
      });
    }

    if (this._stickToPage) {
      setTimeout(() => {
        currentViewer.currentPageNumber = this._page;
      });
    }

    this.updateSize();
  }
/**
 * @param {?} viewportWidth
 * @return {?}
 */
private getScale(viewportWidth: number) {
    const /** @type {?} */ offsetWidth = this.element.nativeElement.offsetWidth;

    if (offsetWidth === 0) {
      return 1;
    }

    return this._zoom * (offsetWidth / viewportWidth) / PdfViewerComponent.CSS_UNITS;
  }
/**
 * @return {?}
 */
private getCurrentViewer(): any {
    return this._showAll ? this.pdfMultiPageViewer : this.pdfSinglePageViewer;
  }
/**
 * @return {?}
 */
private resetPdfDocument() {
    this.pdfFindController.reset();

    if (this._showAll) {
      this.pdfSinglePageViewer.setDocument(null);
      this.pdfSinglePageLinkService.setDocument(null);

      this.pdfMultiPageViewer.setDocument(this._pdf);
      this.pdfMultiPageLinkService.setDocument(this._pdf);
    } else {
      this.pdfMultiPageViewer.setDocument(null);
      this.pdfMultiPageLinkService.setDocument(null);

      this.pdfSinglePageViewer.setDocument(this._pdf);
      this.pdfSinglePageLinkService.setDocument(this._pdf);
    }

  }
/**
 * @return {?}
 */
private async close() {
    if (!this.pdfLoadingTask) {
      return;
    }
    const /** @type {?} */ promise = this.pdfLoadingTask.destroy();
    this.pdfLoadingTask = null;

    if (this._pdf) {
      this._pdf.destroy();
      this._pdf = null;

      this.pdfFindController.reset();
      this.pdfViewer.setDocument(null);
      this.pdfLinkService.setDocument(null);
    }

    return promise;
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'pdf-viewer',
  template: `
    <div class="ng2-pdf-viewer-container">
      <div class="pdfViewer"></div>
    </div>`,
  styles: [`
    .ng2-pdf-viewer-container {
      position: relative;
      overflow: auto;
      width: 100%;
      height: 100%; }

    :host /deep/ .textLayer {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      overflow: hidden;
      opacity: 0.2;
      line-height: 1.0; }
      :host /deep/ .textLayer > div {
        color: transparent;
        position: absolute;
        white-space: pre;
        cursor: text;
        -webkit-transform-origin: 0% 0%;
        transform-origin: 0% 0%; }
      :host /deep/ .textLayer .highlight {
        margin: -1px;
        padding: 1px;
        background-color: #002bff;
        border-radius: 4px; }
        :host /deep/ .textLayer .highlight.begin {
          border-radius: 4px 0px 0px 4px; }
        :host /deep/ .textLayer .highlight.end {
          border-radius: 0px 4px 4px 0px; }
        :host /deep/ .textLayer .highlight.middle {
          border-radius: 0px; }
        :host /deep/ .textLayer .highlight.selected {
          background-color: darkgreen; }
      :host /deep/ .textLayer ::-moz-selection {
        background: #002bff; }
      :host /deep/ .textLayer ::selection {
        background: #002bff; }
      :host /deep/ .textLayer ::-moz-selection {
        background: #002bff; }
      :host /deep/ .textLayer .endOfContent {
        display: block;
        position: absolute;
        left: 0px;
        top: 100%;
        right: 0px;
        bottom: 0px;
        z-index: -1;
        cursor: default;
        -webkit-user-select: none;
        -ms-user-select: none;
        -moz-user-select: none; }
        :host /deep/ .textLayer .endOfContent.active {
          top: 0px; }

    :host /deep/ .annotationLayer section {
      position: absolute; }

    :host /deep/ .annotationLayer .linkAnnotation > a {
      position: absolute;
      font-size: 1em;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: url("data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7") 0 0 repeat;
      /* -ms-a */ }
      :host /deep/ .annotationLayer .linkAnnotation > a:hover {
        opacity: 0.2;
        background: #002bff;
        -webkit-box-shadow: 0px 2px 10px #002bff;
                box-shadow: 0px 2px 10px #002bff; }

    :host /deep/ .annotationLayer .textAnnotation img {
      position: absolute;
      cursor: pointer; }

    :host /deep/ .annotationLayer .textWidgetAnnotation input,
    :host /deep/ .annotationLayer .textWidgetAnnotation textarea,
    :host /deep/ .annotationLayer .choiceWidgetAnnotation select,
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.checkBox input,
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.radioButton input {
      background-color: #002bff;
      border: 1px solid transparent;
      -webkit-box-sizing: border-box;
              box-sizing: border-box;
      font-size: 9px;
      height: 100%;
      padding: 0 3px;
      vertical-align: top;
      width: 100%; }

    :host /deep/ .annotationLayer .textWidgetAnnotation textarea {
      font: message-box;
      font-size: 9px;
      resize: none; }

    :host /deep/ .annotationLayer .textWidgetAnnotation input[disabled],
    :host /deep/ .annotationLayer .textWidgetAnnotation textarea[disabled],
    :host /deep/ .annotationLayer .choiceWidgetAnnotation select[disabled],
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.checkBox input[disabled],
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.radioButton input[disabled] {
      background: none;
      border: 1px solid transparent;
      cursor: not-allowed; }

    :host /deep/ .annotationLayer .textWidgetAnnotation input:hover,
    :host /deep/ .annotationLayer .textWidgetAnnotation textarea:hover,
    :host /deep/ .annotationLayer .choiceWidgetAnnotation select:hover,
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.checkBox input:hover,
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.radioButton input:hover {
      border: 1px solid #000; }

    :host /deep/ .annotationLayer .textWidgetAnnotation input:focus,
    :host /deep/ .annotationLayer .textWidgetAnnotation textarea:focus,
    :host /deep/ .annotationLayer .choiceWidgetAnnotation select:focus {
      background: none;
      border: 1px solid transparent; }

    :host /deep/ .annotationLayer .textWidgetAnnotation input.comb {
      font-family: monospace;
      padding-left: 2px;
      padding-right: 0; }
      :host /deep/ .annotationLayer .textWidgetAnnotation input.comb:focus {
        width: 115%; }

    :host /deep/ .annotationLayer .buttonWidgetAnnotation.checkBox input,
    :host /deep/ .annotationLayer .buttonWidgetAnnotation.radioButton input {
      -webkit-appearance: none;
      -moz-appearance: none;
      -ms-appearance: none;
      appearance: none; }

    :host /deep/ .annotationLayer .popupWrapper {
      position: absolute;
      width: 20em; }

    :host /deep/ .annotationLayer .popup {
      position: absolute;
      z-index: 200;
      max-width: 20em;
      background-color: #FFFF99;
      -webkit-box-shadow: 0px 2px 5px #333;
              box-shadow: 0px 2px 5px #333;
      border-radius: 2px;
      padding: 0.6em;
      margin-left: 5px;
      cursor: pointer;
      word-wrap: break-word; }
      :host /deep/ .annotationLayer .popup h1 {
        font-size: 1em;
        border-bottom: 1px solid #000000;
        padding-bottom: 0.2em; }
      :host /deep/ .annotationLayer .popup p {
        padding-top: 0.2em; }

    :host /deep/ .annotationLayer .highlightAnnotation,
    :host /deep/ .annotationLayer .underlineAnnotation,
    :host /deep/ .annotationLayer .squigglyAnnotation,
    :host /deep/ .annotationLayer .strikeoutAnnotation,
    :host /deep/ .annotationLayer .fileAttachmentAnnotation {
      cursor: pointer; }

    :host /deep/ .pdfViewer .canvasWrapper {
      overflow: hidden; }

    :host /deep/ .pdfViewer .page {
      direction: ltr;
      width: 816px;
      height: 1056px;
      margin: 1px auto -8px auto;
      position: relative;
      overflow: visible;
      border: 9px solid transparent;
      background-clip: content-box;
      -o-border-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAQAAADYWf5HAAAA6UlEQVR4Xl2Pi2rEMAwE16fm1f7/r14v7w4rI0IzLAF7hLxNevBSEMEF5+OilNCsRd8ZMyn+a4NmsOT8WJw1lFbSYgGFzF2bLFoLjTClWjKKGRWpDYAGXUnZ4uhbBUzF3Oe/GG/ue2fn4GgsyXhNgysV2JnrhKEMg4fEZcALmiKbNhBBRFpSyDOj1G4QOVly6O1FV54ZZq8OVygrciDt6JazRgi1ljTPH0gbrPmHPXAbCiDd4GawIjip1TPh9tt2sz24qaCjr/jAb/GBFTbq9KZ7Ke/Cqt8nayUikZKsWZK7Fe6bg5dOUt8fZHWG2BHc+6EAAAAASUVORK5CYII=") 9 9 repeat;
         border-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAQAAADYWf5HAAAA6UlEQVR4Xl2Pi2rEMAwE16fm1f7/r14v7w4rI0IzLAF7hLxNevBSEMEF5+OilNCsRd8ZMyn+a4NmsOT8WJw1lFbSYgGFzF2bLFoLjTClWjKKGRWpDYAGXUnZ4uhbBUzF3Oe/GG/ue2fn4GgsyXhNgysV2JnrhKEMg4fEZcALmiKbNhBBRFpSyDOj1G4QOVly6O1FV54ZZq8OVygrciDt6JazRgi1ljTPH0gbrPmHPXAbCiDd4GawIjip1TPh9tt2sz24qaCjr/jAb/GBFTbq9KZ7Ke/Cqt8nayUikZKsWZK7Fe6bg5dOUt8fZHWG2BHc+6EAAAAASUVORK5CYII=") 9 9 repeat;
      background-color: white; }
      :host /deep/ .pdfViewer .page canvas {
        margin: 0;
        display: block; }
      :host /deep/ .pdfViewer .page .loadingIcon {
        position: absolute;
        display: block;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background: url("data:image/gif;base64,R0lGODlhGAAYAPQAAP///wAAAM7Ozvr6+uDg4LCwsOjo6I6OjsjIyJycnNjY2KioqMDAwPLy8nZ2doaGhri4uGhoaAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJBwAAACwAAAAAGAAYAAAFriAgjiQAQWVaDgr5POSgkoTDjFE0NoQ8iw8HQZQTDQjDn4jhSABhAAOhoTqSDg7qSUQwxEaEwwFhXHhHgzOA1xshxAnfTzotGRaHglJqkJcaVEqCgyoCBQkJBQKDDXQGDYaIioyOgYSXA36XIgYMBWRzXZoKBQUMmil0lgalLSIClgBpO0g+s26nUWddXyoEDIsACq5SsTMMDIECwUdJPw0Mzsu0qHYkw72bBmozIQAh+QQJBwAAACwAAAAAGAAYAAAFsCAgjiTAMGVaDgR5HKQwqKNxIKPjjFCk0KNXC6ATKSI7oAhxWIhezwhENTCQEoeGCdWIPEgzESGxEIgGBWstEW4QCGGAIJEoxGmGt5ZkgCRQQHkGd2CESoeIIwoMBQUMP4cNeQQGDYuNj4iSb5WJnmeGng0CDGaBlIQEJziHk3sABidDAHBgagButSKvAAoyuHuUYHgCkAZqebw0AgLBQyyzNKO3byNuoSS8x8OfwIchACH5BAkHAAAALAAAAAAYABgAAAW4ICCOJIAgZVoOBJkkpDKoo5EI43GMjNPSokXCINKJCI4HcCRIQEQvqIOhGhBHhUTDhGo4diOZyFAoKEQDxra2mAEgjghOpCgz3LTBIxJ5kgwMBShACREHZ1V4Kg1rS44pBAgMDAg/Sw0GBAQGDZGTlY+YmpyPpSQDiqYiDQoCliqZBqkGAgKIS5kEjQ21VwCyp76dBHiNvz+MR74AqSOdVwbQuo+abppo10ssjdkAnc0rf8vgl8YqIQAh+QQJBwAAACwAAAAAGAAYAAAFrCAgjiQgCGVaDgZZFCQxqKNRKGOSjMjR0qLXTyciHA7AkaLACMIAiwOC1iAxCrMToHHYjWQiA4NBEA0Q1RpWxHg4cMXxNDk4OBxNUkPAQAEXDgllKgMzQA1pSYopBgonCj9JEA8REQ8QjY+RQJOVl4ugoYssBJuMpYYjDQSliwasiQOwNakALKqsqbWvIohFm7V6rQAGP6+JQLlFg7KDQLKJrLjBKbvAor3IKiEAIfkECQcAAAAsAAAAABgAGAAABbUgII4koChlmhokw5DEoI4NQ4xFMQoJO4uuhignMiQWvxGBIQC+AJBEUyUcIRiyE6CR0CllW4HABxBURTUw4nC4FcWo5CDBRpQaCoF7VjgsyCUDYDMNZ0mHdwYEBAaGMwwHDg4HDA2KjI4qkJKUiJ6faJkiA4qAKQkRB3E0i6YpAw8RERAjA4tnBoMApCMQDhFTuySKoSKMJAq6rD4GzASiJYtgi6PUcs9Kew0xh7rNJMqIhYchACH5BAkHAAAALAAAAAAYABgAAAW0ICCOJEAQZZo2JIKQxqCOjWCMDDMqxT2LAgELkBMZCoXfyCBQiFwiRsGpku0EshNgUNAtrYPT0GQVNRBWwSKBMp98P24iISgNDAS4ipGA6JUpA2WAhDR4eWM/CAkHBwkIDYcGiTOLjY+FmZkNlCN3eUoLDmwlDW+AAwcODl5bYl8wCVYMDw5UWzBtnAANEQ8kBIM0oAAGPgcREIQnVloAChEOqARjzgAQEbczg8YkWJq8nSUhACH5BAkHAAAALAAAAAAYABgAAAWtICCOJGAYZZoOpKKQqDoORDMKwkgwtiwSBBYAJ2owGL5RgxBziQQMgkwoMkhNqAEDARPSaiMDFdDIiRSFQowMXE8Z6RdpYHWnEAWGPVkajPmARVZMPUkCBQkJBQINgwaFPoeJi4GVlQ2Qc3VJBQcLV0ptfAMJBwdcIl+FYjALQgimoGNWIhAQZA4HXSpLMQ8PIgkOSHxAQhERPw7ASTSFyCMMDqBTJL8tf3y2fCEAIfkECQcAAAAsAAAAABgAGAAABa8gII4k0DRlmg6kYZCoOg5EDBDEaAi2jLO3nEkgkMEIL4BLpBAkVy3hCTAQKGAznM0AFNFGBAbj2cA9jQixcGZAGgECBu/9HnTp+FGjjezJFAwFBQwKe2Z+KoCChHmNjVMqA21nKQwJEJRlbnUFCQlFXlpeCWcGBUACCwlrdw8RKGImBwktdyMQEQciB7oACwcIeA4RVwAODiIGvHQKERAjxyMIB5QlVSTLYLZ0sW8hACH5BAkHAAAALAAAAAAYABgAAAW0ICCOJNA0ZZoOpGGQrDoOBCoSxNgQsQzgMZyIlvOJdi+AS2SoyXrK4umWPM5wNiV0UDUIBNkdoepTfMkA7thIECiyRtUAGq8fm2O4jIBgMBA1eAZ6Knx+gHaJR4QwdCMKBxEJRggFDGgQEREPjjAMBQUKIwIRDhBDC2QNDDEKoEkDoiMHDigICGkJBS2dDA6TAAnAEAkCdQ8ORQcHTAkLcQQODLPMIgIJaCWxJMIkPIoAt3EhACH5BAkHAAAALAAAAAAYABgAAAWtICCOJNA0ZZoOpGGQrDoOBCoSxNgQsQzgMZyIlvOJdi+AS2SoyXrK4umWHM5wNiV0UN3xdLiqr+mENcWpM9TIbrsBkEck8oC0DQqBQGGIz+t3eXtob0ZTPgNrIwQJDgtGAgwCWSIMDg4HiiUIDAxFAAoODwxDBWINCEGdSTQkCQcoegADBaQ6MggHjwAFBZUFCm0HB0kJCUy9bAYHCCPGIwqmRq0jySMGmj6yRiEAIfkECQcAAAAsAAAAABgAGAAABbIgII4k0DRlmg6kYZCsOg4EKhLE2BCxDOAxnIiW84l2L4BLZKipBopW8XRLDkeCiAMyMvQAA+uON4JEIo+vqukkKQ6RhLHplVGN+LyKcXA4Dgx5DWwGDXx+gIKENnqNdzIDaiMECwcFRgQCCowiCAcHCZIlCgICVgSfCEMMnA0CXaU2YSQFoQAKUQMMqjoyAglcAAyBAAIMRUYLCUkFlybDeAYJryLNk6xGNCTQXY0juHghACH5BAkHAAAALAAAAAAYABgAAAWzICCOJNA0ZVoOAmkY5KCSSgSNBDE2hDyLjohClBMNij8RJHIQvZwEVOpIekRQJyJs5AMoHA+GMbE1lnm9EcPhOHRnhpwUl3AsknHDm5RN+v8qCAkHBwkIfw1xBAYNgoSGiIqMgJQifZUjBhAJYj95ewIJCQV7KYpzBAkLLQADCHOtOpY5PgNlAAykAEUsQ1wzCgWdCIdeArczBQVbDJ0NAqyeBb64nQAGArBTt8R8mLuyPyEAOwAAAAAAAAAAAA==") center no-repeat; }

    :host /deep/ .pdfViewer.removePageBorders .page {
      margin: 0px auto 10px auto;
      border: none; }
  `]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'afterLoadComplete': [{ type: Output, args: ['after-load-complete', ] },],
'pageRendered': [{ type: Output, args: ['page-rendered', ] },],
'textLayerRendered': [{ type: Output, args: ['text-layer-rendered', ] },],
'onError': [{ type: Output, args: ['error', ] },],
'onProgress': [{ type: Output, args: ['on-progress', ] },],
'pageChange': [{ type: Output },],
'src': [{ type: Input },],
'cMapsUrl': [{ type: Input, args: ['c-maps-url', ] },],
'page': [{ type: Input, args: ['page', ] },],
'renderText': [{ type: Input, args: ['render-text', ] },],
'renderTextMode': [{ type: Input, args: ['render-text-mode', ] },],
'originalSize': [{ type: Input, args: ['original-size', ] },],
'showAll': [{ type: Input, args: ['show-all', ] },],
'stickToPage': [{ type: Input, args: ['stick-to-page', ] },],
'zoom': [{ type: Input, args: ['zoom', ] },],
'rotation': [{ type: Input, args: ['rotation', ] },],
'externalLinkTarget': [{ type: Input, args: ['external-link-target', ] },],
'autoresize': [{ type: Input, args: ['autoresize', ] },],
'fitToPage': [{ type: Input, args: ['fit-to-page', ] },],
'onPageResize': [{ type: HostListener, args: ['window:resize', [], ] },],
'onPageRendered': [{ type: HostListener, args: ['pagerendered', ['$event'], ] },],
'onTextLayerRendered': [{ type: HostListener, args: ['textlayerrendered', ['$event'], ] },],
};
}

function PdfViewerComponent_tsickle_Closure_declarations() {
/** @type {?} */
PdfViewerComponent.CSS_UNITS;
/** @type {?} */
PdfViewerComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PdfViewerComponent.ctorParameters;
/** @type {?} */
PdfViewerComponent.propDecorators;
/** @type {?} */
PdfViewerComponent.prototype.pdfMultiPageViewer;
/** @type {?} */
PdfViewerComponent.prototype.pdfMultiPageLinkService;
/** @type {?} */
PdfViewerComponent.prototype.pdfMultiPageFindController;
/** @type {?} */
PdfViewerComponent.prototype.pdfSinglePageViewer;
/** @type {?} */
PdfViewerComponent.prototype.pdfSinglePageLinkService;
/** @type {?} */
PdfViewerComponent.prototype.pdfSinglePageFindController;
/** @type {?} */
PdfViewerComponent.prototype._cMapsUrl;
/** @type {?} */
PdfViewerComponent.prototype._renderText;
/** @type {?} */
PdfViewerComponent.prototype._renderTextMode;
/** @type {?} */
PdfViewerComponent.prototype._stickToPage;
/** @type {?} */
PdfViewerComponent.prototype._originalSize;
/** @type {?} */
PdfViewerComponent.prototype._pdf;
/** @type {?} */
PdfViewerComponent.prototype._page;
/** @type {?} */
PdfViewerComponent.prototype._zoom;
/** @type {?} */
PdfViewerComponent.prototype._rotation;
/** @type {?} */
PdfViewerComponent.prototype._showAll;
/** @type {?} */
PdfViewerComponent.prototype._canAutoResize;
/** @type {?} */
PdfViewerComponent.prototype._fitToPage;
/** @type {?} */
PdfViewerComponent.prototype._externalLinkTarget;
/** @type {?} */
PdfViewerComponent.prototype.lastLoaded;
/** @type {?} */
PdfViewerComponent.prototype.resizeTimeout;
/** @type {?} */
PdfViewerComponent.prototype.afterLoadComplete;
/** @type {?} */
PdfViewerComponent.prototype.pageRendered;
/** @type {?} */
PdfViewerComponent.prototype.textLayerRendered;
/** @type {?} */
PdfViewerComponent.prototype.onError;
/** @type {?} */
PdfViewerComponent.prototype.onProgress;
/** @type {?} */
PdfViewerComponent.prototype.pageChange;
/** @type {?} */
PdfViewerComponent.prototype.src;
/** @type {?} */
PdfViewerComponent.prototype.pdfLoadingTask;
/** @type {?} */
PdfViewerComponent.prototype.element;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
